import type { IconName } from '../icons';
import type { MsgKey } from '../../i18n/messages';
import { canViewPath } from '../../services/capabilities';

// The single source of truth for primary navigation. Both nav surfaces
// — the desktop top nav and the phone bottom tab bar — read this one list, so a
// route is added once and appears in the right place(s). The two surfaces show
// curated subsets, not the same set: the top nav carries the fuller menu (incl.
// account settings), while the tab bar is the thumb-reachable destinations.
export interface NavItem {
  href: string;
  labelKey: MsgKey; // top-nav label (message-catalog key, resolved at render)
  tabLabelKey?: MsgKey; // tab-bar label when it differs from `labelKey`
  icon?: IconName; // tab-bar glyph (required for tab-bar items)
  inTopNav?: boolean; // default true
  inTabBar?: boolean; // default false
}

export const NAV_ITEMS: NavItem[] = [
  { href: '/', labelKey: 'nav.home', icon: 'home', inTabBar: true },
  { href: '/#about', labelKey: 'nav.about' },
  { href: '/#supply', labelKey: 'nav.supply' },
  { href: '/#why', labelKey: 'nav.why' },
  { href: '/contact', labelKey: 'nav.contact', icon: 'cases', inTabBar: true },
  { href: '/me', labelKey: 'nav.me', icon: 'me', inTopNav: false, inTabBar: true },
  { href: '/settings/devices', labelKey: 'nav.devices' },
  { href: '/settings/passkeys', labelKey: 'nav.passkeys' },
];

// Prefix-aware active matching so a tab stays lit on its sub-pages — e.g. Me on
// /settings/devices (reached from the Me hub), Module A on a future
// /module-a/:id. '/' matches only itself.
export function isActiveTab(href: string, pathname: string = location.pathname): boolean {
  if (pathname === href) return true;
  if (href === '/') return false;
  if (pathname.startsWith(href + '/')) return true;
  return href === '/me' && pathname.startsWith('/settings/');
}

// The nav surfaces drop any target this user's role + device class can't reach
// (client mirror); open routes (home, account settings) always pass.
export function topNavItems(): NavItem[] {
  return NAV_ITEMS.filter((i) => i.inTopNav !== false && canViewPath(i.href));
}

export function tabBarItems(): NavItem[] {
  return NAV_ITEMS.filter((i) => i.inTabBar === true && canViewPath(i.href));
}
